import { GroupUserRole } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { RootState } from 'store';
import { deepEqulityCheck } from 'modules/common/utils/deepEqualityCheck';
import { permissionsMap } from 'modules/permissions/constants';
import {
  selectAuthAddress,
  selectIsLoggedIn,
} from 'modules/auth/store/selectors';
import {
  selectIsUserInitialized,
  selectUserAddress,
} from 'modules/user/store/selectors';
import {
  selectPersonalAccount,
  selectPersonalAccountJWT,
  selectPersonalAccountJWTLoaded,
  selectPersonalAccountJWTLoading,
} from 'modules/personalAccount/store/selectors';
import {
  selectTeamAccounts,
  selectTeamAccountsState,
} from 'modules/teamAccount/actions/fetchTeamAccounts';
import {
  selectTeamAccountJWT,
  selectTeamAccountJWTLoading,
  selectTeamAccountJWTState,
} from 'modules/teamAccount/actions/fetchTeamAccountJWT';
import { sortTeamAccounts } from 'modules/teamAccount/utils/sortTeamAccounts';

import { INITIAL_ACCOUNT_STATE } from '../const';
import { ISelectSelectedAccountJWTParams } from './types';

export const selectAccountSlice = (state: RootState) => state.account;

export const selectAccountState = createSelector(
  selectAccountSlice,
  selectUserAddress,
  (state, authAddress) => {
    const accountState = authAddress
      ? state[authAddress.toLowerCase()]
      : INITIAL_ACCOUNT_STATE;

    return accountState ?? INITIAL_ACCOUNT_STATE;
  },
);

export const selectTeamAccountAddress = createSelector(
  selectAccountState,
  state => state.teamAccountAddress,
);

export const selectIsAccountPremissionDialogHidden = createSelector(
  selectAccountState,
  state => state.isAccountPermissionDialogHidden,
);

export const selectIsTeamAccountInitialized = createSelector(
  selectAccountState,
  state => state.isTeamAccountInitialized,
);

export const selectIsWorkerAuthorized = createSelector(
  selectAccountState,
  state => state.isWorkerAuthorized,
);

export const selectUserEndpointToken = createSelector(
  selectAccountState,
  state => state.userEndpointToken,
);

export const selectWorkerToken = createSelector(
  selectAccountState,
  state => state.workerToken,
);

export const selectAccountAddress = createSelector(
  selectAuthAddress,
  selectUserAddress,
  selectTeamAccountAddress,
  (authAddress, userAddress, teamAccountAddress) =>
    teamAccountAddress || authAddress || userAddress,
);

export const selectIsTeamAccount = createSelector(
  selectTeamAccountAddress,
  teamAccountAddress => Boolean(teamAccountAddress),
);

export const selectIsPersonalAccount = createSelector(
  selectIsTeamAccount,
  isTeamAccount => !isTeamAccount,
);

export const selectAccounts = createSelector(
  selectTeamAccounts,
  selectPersonalAccount,
  (teamAccounts, personalAccount) => {
    if (personalAccount) {
      return [personalAccount, ...teamAccounts];
    }

    return teamAccounts;
  },
);

export const selectSortedTeamAccounts = createSelector(
  selectTeamAccounts,
  selectTeamAccountAddress,
  (teamAccounts, selectedTeamAccountAddress) =>
    sortTeamAccounts({ selectedTeamAccountAddress, teamAccounts }),
);

export const selectAccount = createSelector(
  selectAccountAddress,
  selectAccounts,
  (accountAddress, accounts) =>
    accounts.find(({ address }) => address === accountAddress),
);

export const selectAccountName = createSelector(
  selectAccount,
  account => account?.name,
);

export const selectAccountRole = createSelector(
  selectAccount,
  account => account?.role,
);

export const selectAccountPermissions = createSelector(
  selectAccountRole,
  role => (role ? permissionsMap[role] : []),
);

export const selectAccountPermissionsInitialized = createSelector(
  selectTeamAccountsState,
  selectIsTeamAccount,
  selectIsUserInitialized,
  ({ isSuccess: teamAccountsLoaded }, isTeamAccount, isUserInitialized) =>
    isTeamAccount ? teamAccountsLoaded : isUserInitialized,
);

export const selectIsAdmin = createSelector(
  selectAccountRole,
  role => role === GroupUserRole.admin,
);

export const selectIsDev = createSelector(
  selectAccountRole,
  role => role === GroupUserRole.dev,
);

export const selectIsFinance = createSelector(
  selectAccountRole,
  role => role === GroupUserRole.finance,
);

export const selectIsOwner = createSelector(
  selectAccountRole,
  role => role === GroupUserRole.owner,
);

export const selectTeamAccount = createSelector(
  selectTeamAccountAddress,
  selectAccounts,
  (teamAccountAddress, accounts) => {
    if (teamAccountAddress) {
      return accounts.find(({ address }) => address === teamAccountAddress);
    }

    return undefined;
  },
);

export const selectIsTeamAccountExist = createSelector(
  selectTeamAccount,
  teamAccount => Boolean(teamAccount),
);

export const selectSelectedAccountJWT = createSelector(
  selectIsTeamAccount,
  (state: RootState, { authAddress }: ISelectSelectedAccountJWTParams) =>
    selectPersonalAccountJWT(state, authAddress!),
  (state: RootState, { group }: ISelectSelectedAccountJWTParams) =>
    selectTeamAccountJWT(state, { group: group! }),
  (isTeamAccount, personalAccountJWT, groupAccountJWT) =>
    isTeamAccount ? groupAccountJWT : personalAccountJWT,
  {
    argsMemoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectSelectedAccountJWTLoading = createSelector(
  (state: RootState, { authAddress }: ISelectSelectedAccountJWTParams) =>
    selectPersonalAccountJWTLoading(state, authAddress!),
  (state: RootState, { group }: ISelectSelectedAccountJWTParams) =>
    selectTeamAccountJWTLoading(state, { group: group! }),
  (personalAccountJWTLoading, teamAccountJWTLoading) =>
    personalAccountJWTLoading || teamAccountJWTLoading,
  {
    argsMemoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectSelectedAccountJWTLoaded = createSelector(
  (state: RootState, { authAddress }: ISelectSelectedAccountJWTParams) =>
    selectPersonalAccountJWTLoaded(state, authAddress!),
  (state: RootState, { group }: ISelectSelectedAccountJWTParams) =>
    selectTeamAccountJWTState(state, { group: group! }).isSuccess,
  (personalAccountJWTLoaded, teamAccountJWTLoaded) =>
    personalAccountJWTLoaded || teamAccountJWTLoaded,
  {
    argsMemoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectIsWorkerAuthorizing = createSelector(
  selectSelectedAccountJWTLoaded,
  selectIsWorkerAuthorized,
  (isAccountJWTLoaded, isWorkerAuthorized) =>
    isAccountJWTLoaded && !isWorkerAuthorized,
);

export const selectIsAccountInitialized = selectIsWorkerAuthorized;

export const selectIsAccountInitializing = createSelector(
  selectIsLoggedIn,
  selectIsTeamAccountInitialized,
  selectSelectedAccountJWTLoading,
  selectIsWorkerAuthorizing,
  (
    isLoggedIn,
    isTeamAccountInitialized,
    accountJWTLoading,
    isWorkerAuthorizing,
    // eslint-disable-next-line max-params
  ) => {
    if (!isLoggedIn) {
      return false;
    }

    return (
      !isTeamAccountInitialized || accountJWTLoading || isWorkerAuthorizing
    );
  },
);
